import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import { LinkedInspection } from "../api/WorkOrders";
import { find, remove, unionBy } from "lodash";
import { InspectionData } from "./wo-order";
import { Inspection } from "../components/InspectionGroupList";

type InspectionInfo = {
  equipmentId?: string;
} & Inspection;

type EquipmentState = {
  inspections: { [equipId: string]: LinkedInspection[] };
  inspectionData: { [equipId: string]: InspectionData[] };
  selectedInspection: InspectionInfo[];
  downloadedInspection: InspectionInfo[];
};

type EquipmentAction = {
  setInspectionsForEquipment: (
    equipId: string,
    inspections: LinkedInspection[]
  ) => void;
  getInspectionsForEquipment: (
    equipId: string
  ) => LinkedInspection[] | undefined;
  toggleSelectedInspection: (equip: InspectionInfo) => void;
  setDownloadedInspections: (items: InspectionInfo[]) => void;
  setEquipInspectionData: (equipId: string, data: InspectionData) => void;
  getInspectionDataForEquip: (
    equipId: string,
    inspectionId: string
  ) => InspectionData | undefined;
  markInspectionCompleted: (equipId: string, inspectionId: string) => void;
  //reset
  resetAllButDownloadedInspections: () => void;
  resetSelectedInspections: () => void;
  reset: () => void;
};

const defaultState: EquipmentState = {
  inspections: {},
  downloadedInspection: [],
  selectedInspection: [],
  inspectionData: {},
};

export const useEquipmentStore = create<EquipmentState & EquipmentAction>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        getInspectionsForEquipment(equipId) {
          return get().inspections[equipId];
        },
        setInspectionsForEquipment(equipId, inspections) {
          set((state) => {
            state.inspections = {
              ...state.inspections,
              [equipId]: inspections,
            };
          });
        },
        toggleSelectedInspection(equip) {
          set((state) => {
            const selectedEquips = [...state.selectedInspection];
            const existingEquip = find(selectedEquips, {
              id: equip.id,
              equipmentId: equip.equipmentId,
            });

            if (existingEquip)
              remove(selectedEquips, {
                id: equip.id,
                equipmentId: equip.equipmentId,
              });
            else selectedEquips.push(equip);
            state.selectedInspection = selectedEquips;
          });
        },
        markInspectionCompleted(equipId, inspectionId) {
          const { inspectionData, setEquipInspectionData } = get();
          const insp = inspectionData[equipId]?.find(
            (i) => i.inspectionId?.toString() === inspectionId.toString()
          );
          if (insp) {
            setEquipInspectionData(equipId, { ...insp, isCompleted: true });
          }
        },
        setDownloadedInspections(items) {
          set((state) => {
            state.downloadedInspection = unionBy(
              items,
              [...state.downloadedInspection],
              (i) => `${i.equipmentId}-${i.id}`
            );
          });
        },
        resetSelectedInspections() {
          set((state) => {
            state.selectedInspection = [];
          });
        },
        resetAllButDownloadedInspections() {
          set((state) => {
            const equipIdsToKeep = state.downloadedInspection.map(
              (d) => d.equipmentId
            );
            const retainKeys = (obj: Record<string, any>) => {
              return Object.fromEntries(
                Object.entries(obj)
                  .filter(([key]) => equipIdsToKeep.includes(key))
                  .map(([key, value]) => {
                    if (Array.isArray(value)) {
                      const downloadedInspections =
                        state.downloadedInspection.filter(
                          (i) => i.equipmentId === key
                        );
                      const inspectionsToRetain = value.filter((v) =>
                        downloadedInspections.some(
                          (d) => d.id.toString() === v.inspectionId.toString()
                        )
                      );
                      return [key, inspectionsToRetain];
                    }
                    return [key, value];
                  })
              );
            };
            state.inspections = {}; //remove all the inspection
            state.inspectionData = retainKeys(state.inspectionData);
          });
        },
        setEquipInspectionData(equipId, data) {
          set((state) => {
            state.inspectionData = {
              ...state.inspectionData,
              [equipId]: [
                ...((state.inspectionData[equipId] ?? []).filter(
                  (i) => i.inspectionId !== data.inspectionId
                ) ?? []),
                data,
              ],
            };
          });
        },
        getInspectionDataForEquip(equipId, inspectionId) {
          const inspectionsForEquip = get().inspectionData[equipId];
          if (!inspectionsForEquip) {
            return undefined;
          }
          return inspectionsForEquip.find(
            (i) => i.inspectionId === inspectionId
          );
        },
        reset() {
          set(() => defaultState);
        },
      })),
      {
        name: "equipment",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          inspectionData: state.inspectionData,
          downloadedInspection: state.downloadedInspection,
        }),
      }
    ),
    {
      name: "EquipmentStore",
      enabled: true,
    }
  )
);
